import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CreateUser } from '../components/users/createuser';
import { ParticipantRegistrationConfig } from '../components/users/participant-registration-config';
import { UserQueryFilter, UsersList } from '../components/users/user-list';
import { AccessControl } from '../components/users/access-control';
import { activateUser } from '../components/users/activate-user';
import { UpdateUser } from '../components/users/update-user';
import { AuthService } from './auth.service';
import { param } from 'jquery';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    apiUrl = environment.apiUrl

    httpOptions = {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    constructor(private http: HttpClient, private _authservice: AuthService) { }

    getUserQueryParams(filter: UserQueryFilter): HttpParams {
        const cleaned: Record<string, string | number> = {};
        Object.entries(filter).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') {
                return;
            }
            cleaned[key] = value as string | number;
        });
        return new HttpParams({ fromObject: cleaned });
    }

    getApplicationRolesForAddUser(): Observable<any> {
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        return this.http.get<any>(this.apiUrl + "ApplicationRoles/getApplicationRolesForAddUser", { headers: headers });
    }

    getApplicationRolesForUpdateUser(userid: string): Observable<any> {
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        let params = new HttpParams()
        params = params.append("userid", userid)
        return this.http.get<any>(this.apiUrl + "ApplicationRoles/getApplicationRolesForUpdateUser", { headers: headers, params: params });
    }

    getApplicationRolesForUserFiltering(): Observable<any> {
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        const params = new HttpParams().set('programMode', this._authservice.getEffectiveProgramMode());
        return this.http.get<any>(this.apiUrl + "ApplicationRoles/getRolesForUserFiltering", { headers: headers, params: params });
    }

    getApplicationRolesByUserId(userid: number): Observable<AccessControl> {
        let params = new HttpParams()
        params = params.append("userid", userid.toString())
        return this.http.get<AccessControl>(this.apiUrl + "ApplicationRoles/getRolesByUserId", { params: params });
    }

    getAccessControlForUser(): Observable<AccessControl> {

        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        return this.http.get<AccessControl>(this.apiUrl + "User/getAccessForUser", { headers: headers })
    }

    private toCreateUserPayload(createUser: CreateUser) {
        return {
            ...createUser,
            organization: createUser.organization ? createUser.organization : null,
            branch: createUser.branch || null,
            province: createUser.province || null,
        };
    }

    createUser(createUser: CreateUser): Observable<any> {
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({
            'Authorization': 'Bearer ' + token,
            'Content-Type': 'application/json',
        });
        return this.http.post<any>(this.apiUrl + "User/createUser", this.toCreateUserPayload(createUser), { headers });
    }

    getAllUsers(filter: UserQueryFilter): Observable<any> {
      let HttpParams = this.getUserQueryParams(filter);
      const token = this._authservice.getAuthToken();
      const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
      return this.http.get<UsersList[]>(this.apiUrl + "User/getUsers", { headers: headers, params: HttpParams, observe: 'response' });
    }

    getUserDetailsForUpdate(userid: string): Observable<any> {
        let params = new HttpParams()
        params = params.append("userid", userid)
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        return this.http.get<UsersList>(this.apiUrl + "User/getUserDetailsForUpdate", { headers: headers, params: params });
    }

    getUserDetailsForView(userid: string): Observable<any> {
        let params = new HttpParams()
        params = params.append("userid", userid)
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        return this.http.get<UsersList>(this.apiUrl + "User/getUserDetailsForView", { headers: headers, params: params });
    }

    changeUserStatus(userstatus: UpdateUser): Observable<any> {
        return this.http.post<any>(this.apiUrl + "User/changeUserStatus", userstatus);
    }

    activateUser(activationDetails: activateUser): Observable<any> {
        return this.http.post<boolean>(this.apiUrl + "User/activateUser", activationDetails);
    }

    updateUser(userstatus: UpdateUser): Observable<any> {
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        return this.http.post<any>(this.apiUrl + "User/updateUser", userstatus, { headers: headers });
    }

    getUserDetailsFromGuid(guid: string): Observable<any> {
        let params = new HttpParams()
        params = params.append("activationGuid", guid.toString())
        return this.http.get<any>(this.apiUrl + "User/getUserDetailsFromActivationGuid", { params: params })
    }

    getCoachedUsers(): Observable<any> {
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        return this.http.get<any[]>(this.apiUrl + "User/getCoachedUsers",{headers:headers});
    }

    getLLTTFFacilitatorUsers(): Observable<any> {
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        return this.http.get<any[]>(this.apiUrl + "User/getLLTTFFacilitatorUsers", { headers });
    }

    registerBBCoachedParticipant(createuserobj: CreateUser, invitationGuid: string): Observable<any> {
        let params = new HttpParams();
        params = params.append('invitationGuid', invitationGuid);
        return this.http.post<any>(this.apiUrl + "User/registerBBCoachedParticipant", this.toCreateUserPayload(createuserobj), { params: params });
    }

    registerLLTTFCoachedParticipant(createuserobj: CreateUser, verificationguid: string): Observable<any> {
        let params = new HttpParams();
        params = params.append('verificationGuid', verificationguid);
        return this.http.post<any>(this.apiUrl + "User/registerLLTTFCoachedParticipant", this.toCreateUserPayload(createuserobj), { params: params });
    }

    getParticipantRegistrationConfig(): Observable<ParticipantRegistrationConfig> {
        return this.http.get<ParticipantRegistrationConfig>(
            this.apiUrl + "User/getParticipantRegistrationConfig"
        );
    }

    registerBBSelfGuidedParticipant(createuserobj: CreateUser): Observable<any> {
        return this.http.post<any>(this.apiUrl + "User/registerBBSelfGuidedParticipant", this.toCreateUserPayload(createuserobj));
    }

    getUserAuditLogs(userid: string): Observable<any> {
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        let params = new HttpParams();
        params = params.append('userId', userid);
        return this.http.get(this.apiUrl + 'User/getUserAuditLogs', { headers: headers, params: params });
    }

    deleteUser(userid: string): Observable<any>
    {
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        let params = new HttpParams();
        params = params.append('userId', userid);
        return this.http.delete<any>(this.apiUrl + 'User/deleteUser', { headers: headers, params: params });
    }

    getUserProfile():Observable<any>
    {
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        return this.http.get<any>(this.apiUrl + "User/getUserProfile",{headers:headers});
    }

    updateParticipantProfile(userstatus: UpdateUser): Observable<any> {
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        return this.http.post<any>(this.apiUrl + "User/updateParticipantProfile", userstatus, { headers: headers });
    }

    updateAdminProfile(userstatus: UpdateUser): Observable<any> {
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        return this.http.post<any>(this.apiUrl + "User/updateAdminProfile", userstatus, { headers: headers });
    }

    setParticipantLastActiveRole(lastActiveRole:number):Observable<any>
    {
        const token = this._authservice.getAuthToken();
        const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
        let params = new HttpParams();
        params = params.append('lastActiveRole', lastActiveRole.toString());
        return this.http.put<any>(this.apiUrl + "User/setParticipantLastActiveRole",null,{headers:headers,params:params})
    }

   setUserLanguage(langid:number):Observable<any>
   {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams();
    params = params.append('languageId', langid.toString());
    return this.http.put<any>(this.apiUrl + "User/setUserLastActiveLanguage",null,{headers:headers,params:params})
   }



}
